import { Form, Modal, ModalProps, notification, Tag } from 'antd';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useRouter } from 'next/router';
import { CustomTagProps } from 'rc-select/es/BaseSelect';
import React, { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { buildTimestampDateTime } from '../../helpers/formatters';
import { getAttendeeStatus, useCurrentUser } from '../../helpers/utils';
import {
  Core_KnowledgeInputMsg,
  Knowledge_KnowledgeDataset,
  Resource_AppointmentMsg,
  Resource_AppointmentStatus,
  useCancelAppointmentMutation,
} from '../../src/generated/graphql';
import { AveliosButton } from '../common-layout/avelios-button';
import { GeneralKnowledgeBaseMultiInput } from '../common-layout/general-knowledge-input';
import styles from './appointment-details.module.scss';

interface CancelAppointmentModalProps extends ModalProps {
  appointment: Maybe<Resource_AppointmentMsg> | undefined;
  clinicId: Maybe<string> | undefined;
}

export const CancelAppointmentModal: FC<CancelAppointmentModalProps> = ({
  appointment,
  clinicId,
  ...props
}) => {
  const { formatMessage } = useIntl();

  const router = useRouter();

  const { currentUser, userLoading } = useCurrentUser();

  const [reasonForCancellation, setReasonForCancellation] = useState<
    Maybe<(Maybe<Core_KnowledgeInputMsg> | undefined)[]> | undefined
  >();

  const [cancelAppointmentMutation, { loading: cancelLoading }] = useCancelAppointmentMutation({
    onCompleted: (data) => {
      if (
        getAttendeeStatus(data?.cancelAppointment?.appointment, currentUser?.id) ===
        Resource_AppointmentStatus.AppointmentStatusCancelled
      ) {
        setReasonForCancellation([]);
        // @ts-ignore
        props.onCancel();
        const date = buildTimestampDateTime(appointment?.startTime);
        notification.info({
          message: formatMessage({ id: 'cancel-appointment.success' }, { date }),
        });
        router.push('/appointments');
      } else {
        notification.error({
          message: formatMessage({ id: 'notification.error' }),
        });
      }
    },
    onError: () => {
      notification.error({
        message: formatMessage({ id: 'notification.error' }),
      });
    },
  });
  function cancelAppointment() {
    if (currentUser?.id) {
      cancelAppointmentMutation({
        variables: {
          clinicId: clinicId!,
          portalUserId: currentUser.id,
          request: {
            appointmentId: appointment?.id,
            // @ts-ignore
            reasonsForCancellation: reasonForCancellation,
          },
        },
        refetchQueries: ['GetAppointmentHistory'],
      });
    }
  }

  const tagRender = (props: CustomTagProps) => {
    const { label, closable, onClose } = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        className={styles.reasonForCancellationTag}
        color={'default'}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
      >
        {label}
      </Tag>
    );
  };

  const loading = cancelLoading || userLoading;

  return (
    <Modal
      title={formatMessage({ id: 'cancel-appointment.title' })}
      footer={
        <AveliosButton loading={loading} type={'primary'} danger onClick={cancelAppointment}>
          {formatMessage({ id: 'appointment-cancel' })}
        </AveliosButton>
      }
      {...props}
    >
      <Form layout={'vertical'}>
        <Form.Item label={formatMessage({ id: 'cancel-appointment.reason' })}>
          <GeneralKnowledgeBaseMultiInput
            className={styles.reasonForCancellation}
            // @ts-ignore
            values={reasonForCancellation}
            dataset={Knowledge_KnowledgeDataset.DatasetReasonForPatientAppointmentCancellation}
            loading={loading}
            tagRender={tagRender}
            size={'large'}
            setValues={setReasonForCancellation}
            clinicId={clinicId}
            placeholder={formatMessage({
              id: 'reason-for-cancellation.placeholder',
            })}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
